import { Box, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";
import compInfos from "./sendCalls.json";

import { Link, useLocation } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { dateFormatter } from "../../../utils/date-formatter";
import CustomTable from "../../common/tables/Table";

import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";

import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";

import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import TextFilter from "../../common/tables/filters/TextFilter";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import useUrlSort from "../../../hooks/useUrlSort";
import { SendCallsListRequest } from "../../../services/Endpoints/send-calls/SendCallsEndpoint";
import UserLink from "../common/UserLink";
import {
  PartnerListColumnFilterable,
  PartnerListColumnSortable,
} from "../../../services/Endpoints/partners/PartnerEndpoint";
import { useUrl } from "../../../hooks/urlEncoding";
import { routerPaths } from "../../../constants/Paths";
import {
  BroadcastingListColumnFilterable,
  BroadcastingListColumnSortable,
} from "../../../services/Endpoints/broadcast/BroadcastingListEndpoint";
import {
  NumberListColumnFilterable,
  NumberListColumnSortable,
} from "../../../services/Endpoints/broadcast/NumbersEndpoint";
import LinkWithPopover from "../common/LinkWithPopover";
import {
  LogListColumnFilterable,
  LogListColumnSortable,
} from "../../../services/Endpoints/logs/LogListEndpoints";
import { DocumentText } from "iconsax-react";

export interface ISendCallsProps {
  reload: boolean;
  setReload: (payload: boolean) => void;
}

export default function SendCallsTableComponent({
  reload,
  setReload,
}: Readonly<ISendCallsProps>) {
  const { queryStringUrl, parseQueryString, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const { lang } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [dataTransaction, setDataTransaction] = useState<any[] | []>([]);
  const [total, setTotal] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const { can } = useCanAccess();
  const location = useLocation();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();

  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const listRequest = new SendCallsListRequest(protectedInstance);
      const resp = await listRequest.list({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.send_calls.map(
        ({
          created_at,
          bulk_id,
          number,
          partner_id,
          voice_broadcast_id,
          voice_broadcast_number_id,
          id,
        }) => ({
          id,
          bulk_id,
          number,
          partner_id: partner_id && (
            <UserLink
              disabled={!can(AccessEnums.AdminPartnerList)}
              user={{ id: partner_id }}
              link={queryStringUrl(routerPaths.PARTNERS, {
                filters: {
                  [PartnerListColumnFilterable.ID]: {
                    column: PartnerListColumnFilterable.ID,
                    value: partner_id,
                    operator: FilterOperatorEnum.EQUAL,
                  },
                },
                sorts: {
                  [PartnerListColumnSortable.ID]: {
                    column: PartnerListColumnSortable.ID,
                    direction: SortDirectionEnum.DESC,
                  },
                },
              })}
            />
          ),
          voice_broadcast_id: voice_broadcast_id && (
            <UserLink
              disabled={!can(AccessEnums.AdminVoiceBroadcastsList)}
              user={{ id: voice_broadcast_id }}
              link={queryStringUrl(routerPaths.VOICE_BROADCASTS, {
                filters: {
                  [BroadcastingListColumnFilterable.ID]: {
                    column: BroadcastingListColumnFilterable.ID,
                    value: voice_broadcast_id,
                    operator: FilterOperatorEnum.EQUAL,
                  },
                },
                sorts: {
                  [BroadcastingListColumnSortable.ID]: {
                    column: BroadcastingListColumnSortable.ID,
                    direction: SortDirectionEnum.DESC,
                  },
                },
              })}
            />
          ),
          voice_broadcast_number_id: voice_broadcast_number_id && (
            <UserLink
              disabled={!can(AccessEnums.AdminVoiceBroadcastNumbersList)}
              user={{ id: voice_broadcast_number_id }}
              link={queryStringUrl(routerPaths.BROAD_NUMBER, {
                filters: {
                  [NumberListColumnFilterable.ID]: {
                    column: NumberListColumnFilterable.ID,
                    value: voice_broadcast_number_id,
                    operator: FilterOperatorEnum.EQUAL,
                  },
                },
                sorts: {
                  [NumberListColumnSortable.ID]: {
                    column: NumberListColumnSortable.ID,
                    direction: SortDirectionEnum.DESC,
                  },
                },
              })}
            />
          ),
          created_at,
          operations: (
            <Stack
              sx={{
                mx: "auto",
                display: "flex",
                width: "fit-content",
                justifyContent: "centner",
              }}
            >
              <LinkWithPopover
                title="لاگ ها"
                disabled={!can(AccessEnums.AdminLogsList)}
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.LOGS, {
                    filters: {
                      [LogListColumnFilterable.MODEL]: {
                        column: LogListColumnFilterable.MODEL,
                        operator: FilterOperatorEnum.EQUAL,
                        value: "send_calls",
                      },
                      [LogListColumnFilterable.REFERENCE_MODEL]: {
                        column: LogListColumnFilterable.REFERENCE_MODEL,
                        operator: FilterOperatorEnum.EQUAL,
                        value: id,
                      },
                    },
                    sorts: {
                      [LogListColumnSortable.ID]: {
                        column: LogListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <DocumentText size="20" />
                </Stack>
              </LinkWithPopover>
            </Stack>
          ),
        })
      );
      setDataTransaction(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, [reload, page, rowsPerPage, location.search]);

  const tInfo = compInfos[lang];
  const colName = tInfo.columns;

  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "number",
      label: "شماره",
      minWidth: 120,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "bulk_id",
      label: "شناسه دسته",
      minWidth: 120,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "partner_id",
      label: "شناسه شریک",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "voice_broadcast_id",
      label: "شناسه پیام‌صوتی",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "voice_broadcast_number_id",
      label: "شناسه شماره پیام‌صوتی",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "created_at",
      label: "تاریخ ایجاد",
      minWidth: 100,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "operations",
      label: colName.operations,
      align: "center",
    },
  ];

  const { filtersSchema, columns } = useTableSchema(_columns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <Box
        sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}
      ></Box>

      <CustomTable
        reloadAction={() => setReload(!reload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataTransaction}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
